import { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { financeApi, formatCents, PAYABLE_KIND_LABEL } from "@/services/finance";
import { PageShell, LoadingState } from "@/shared/ui/PageHeader";
import { Card } from "@/shared/ui/Card";

/** 财务总览 · Dashboard（/admin/finance · canvas page-71 · 说明书对齐 v1）。 */
// 近 12 个月（含本月·倒序）· YYYY-MM
const NOW = new Date();
const MONTHS = Array.from({ length: 12 }, (_, i) => {
  const d = new Date(NOW.getFullYear(), NOW.getMonth() - i, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
});

export function FinanceOverviewPage() {
  // 选月份（缺省本月）· ⚠️ 真实按月取数待后端：/_overview 现只返本月·需加 ?period= 参数（后端自检项）
  const [month, setMonth] = useState(MONTHS[0]);
  const isCurrentMonth = month === MONTHS[0];

  const ovQ = useQuery({ queryKey: ["finance", "overview", month], queryFn: () => financeApi.overview(month) });
  const dashQ = useQuery({ queryKey: ["finance", "dashboard"], queryFn: financeApi.dashboard });
  const plQ = useQuery({ queryKey: ["finance", "pl", month], queryFn: () => financeApi.plReport(month) });
  const cfQ = useQuery({ queryKey: ["finance", "cashflow", month], queryFn: () => financeApi.cashflow(month) });
  const apQ = useQuery({ queryKey: ["finance", "payables", "all"], queryFn: () => financeApi.payables() });
  const arQ = useQuery({ queryKey: ["finance", "receivables", "all"], queryFn: () => financeApi.receivables() });

  if (ovQ.isLoading) return <LoadingState />;
  if (ovQ.isError) return (
    <div className="flex flex-col items-center justify-center gap-2 p-12">
      <p className="text-[13px] font-semibold text-red-700">财务总览加载失败</p>
      <p className="text-[11px] text-red-500">{(ovQ.error as Error)?.message || "网络错误"}</p>
      <button onClick={() => ovQ.refetch()} disabled={ovQ.isFetching} className="mt-1 rounded-md bg-blue-600 px-3 py-1.5 text-[12px] font-semibold text-white hover:bg-blue-700 disabled:opacity-40">{ovQ.isFetching ? "重试中…" : "点击重试"}</button>
    </div>
  );
  const ov = ovQ.data;
  const pl = plQ.data;
  const cf = cfQ.data;
  const revenue = pl?.revenue_cents ?? ov?.total_revenue_mtd_cents ?? 0;
  const expense = pl ? pl.cost_cents + (pl.operating_expenses_cents ?? 0) : null;
  const netProfit = pl?.net_profit_cents ?? null;
  const apPending = (apQ.data ?? []).filter((p) => p.status === "pending_approval");
  const arOverdueRows = (arQ.data ?? []).filter((r) => r.days_overdue > 0);
  const cash = ov?.cash_position_cents ?? 0;
  const arOverdue = dashQ.data?.ar_overdue_cents ?? 0;

  // 净利率：后端算（pl.net_margin_pct·单一真源·经理遮罩→null）
  const netMargin = pl?.net_margin_pct == null ? "—" : `${pl.net_margin_pct}%`;
  // KPI · 每项 canvas 真色（说明书 §三 #6）· raw=数值（判负数红字）
  const kpis = [
    { label: "本月收入", raw: revenue, value: formatCents(revenue), sub: "MTD 累计 · 环比/同比待接", color: "text-emerald-500" },
    { label: "本月支出", raw: expense, value: expense == null ? "—" : formatCents(expense), sub: "成本 + 运营费 · 构成待接", color: "text-red-500" },
    { label: "净利润", raw: netProfit, value: netProfit == null ? "—" : formatCents(netProfit), sub: `净利率 ${netMargin}`, color: "text-blue-600" },
    { label: "现金流", raw: cf?.net_cents ?? null, value: cf ? formatCents(cf.net_cents) : "—", sub: "本月净流入 USD", color: "text-violet-600" },
    { label: "待收", raw: ov?.ar_outstanding_cents ?? null, value: formatCents(ov?.ar_outstanding_cents), sub: `累计余额 · ${arOverdue > 0 ? `🔴 逾期 ${formatCents(arOverdue)}` : "无逾期"}`, color: "text-amber-500" },
    { label: "待付", raw: ov?.ap_outstanding_cents ?? null, value: formatCents(ov?.ap_outstanding_cents), sub: `累计余额 · ${apPending.length ? `🟡 应付待审 ${apPending.length} 笔` : "无待审"}`, color: "text-amber-500" },
  ];

  const months = cf?.monthly ?? [];
  const maxIn = Math.max(1, ...months.map((m) => m.in_cents));
  const trend = pl?.margin_trend ?? [];

  return (
    <PageShell
      title="💸 财务总览 · Dashboard"
      subtitle="本月经营全景 · 老板 + 财务 全权限 · 经理仅看客户相关财务（Q7C）"
      actions={
        <div className="flex items-center gap-2">
          {!isCurrentMonth && <span className="text-[10px] font-medium text-slate-400">演示数据 · 真账接 QB 后替换</span>}
          <select value={month} onChange={(e) => setMonth(e.target.value)} className="h-9 rounded-lg border border-slate-200 bg-white px-3 text-[12px] font-medium text-slate-700 focus:border-blue-500 focus:outline-none">
            {MONTHS.map((m) => <option key={m} value={m}>{m}{m === MONTHS[0] ? "（本月）" : ""}</option>)}
          </select>
          <span className="flex h-9 items-center rounded-lg border border-slate-200 bg-white px-3 text-[12px] font-medium text-slate-400">USD</span>
          <Link to="/admin/finance/reports" className="flex h-9 items-center rounded-lg bg-blue-600 px-3 text-[12px] font-semibold text-white hover:bg-blue-700">导出月报</Link>
        </div>
      }
    >
      {/* 权限提示条 · 琥珀 #92400E（说明书 §三 #3） */}
      <div className="rounded-lg bg-amber-50 px-3 py-2 text-[11px] font-semibold text-amber-800">🔒 财务模块全权限：老板 + 财务 · 经理可见范围：仅应收账款 · 客户相关数据（只读）</div>

      {/* 6 KPI */}
      <div className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-6">
        {kpis.map((k) => {
          const neg = k.raw != null && k.raw < 0;  // 亏损/净流出 → 红字加粗
          return (
            <div key={k.label} className="rounded-xl border border-slate-200 bg-white p-4">
              <div className="text-[11px] font-semibold text-slate-500">{k.label}</div>
              <div className={`mt-1.5 text-[22px] font-bold leading-none ${neg ? "text-red-600" : k.color}`}>{k.value}</div>
              <div className="mt-1.5 text-[10px] text-slate-400">{k.sub}</div>
            </div>
          );
        })}
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        {/* 现金流趋势 · 近6月月度柱（canvas page-71） */}
        <Card title="📈 现金流趋势（近 6 月 USD）" action={<span className="text-[12px] text-slate-500">柱=月度流入 · 最新月高亮</span>}>
          {cfQ.isLoading ? <LoadingState /> : cfQ.isError ? (
            <div className="py-6 text-center">
              <p className="text-[12px] font-semibold text-red-700">现金流加载失败</p>
              <button onClick={() => cfQ.refetch()} disabled={cfQ.isFetching} className="mt-1.5 rounded-md bg-blue-600 px-3 py-1 text-[12px] font-semibold text-white hover:bg-blue-700 disabled:opacity-40">{cfQ.isFetching ? "重试中…" : "点击重试"}</button>
            </div>
          ) : months.length === 0 ? (
            <div className="py-6 text-center text-[13px] text-slate-400">暂无现金流数据</div>
          ) : (
            <>
              <div className="flex h-32 items-end gap-3">
                {months.map((m, i) => {
                  const last = i === months.length - 1;
                  return (
                    <div key={i} className="flex flex-1 flex-col items-center justify-end gap-1">
                      <div className={`text-[10px] font-bold ${last ? "text-emerald-500" : "text-blue-500"}`}>${Math.round(m.in_cents / 100 / 1000)}k</div>
                      <div className={`w-full rounded-t ${last ? "bg-emerald-500" : "bg-blue-500"}`} style={{ height: `${Math.max(4, (m.in_cents / maxIn) * 90)}px` }} />
                      <div className="text-[10px] text-slate-500">{m.label}</div>
                    </div>
                  );
                })}
              </div>
              <div className="mt-2 flex justify-between text-[11px]">
                <span className="text-slate-400">近 6 月平均：{formatCents(cf?.avg_in_cents)}</span>
                <span className="font-semibold text-emerald-600">趋势：稳步上升 +{cf?.yoy_pct ?? 0}% YoY</span>
              </div>
            </>
          )}
        </Card>

        {/* 利润率走势 · 近月毛利/净利（canvas page-71） */}
        <Card title="💼 利润率走势" action={<span className="text-[12px] text-slate-500">毛利率 vs 净利率</span>}>
          {plQ.isLoading ? <LoadingState /> : plQ.isError ? (
            <div className="py-6 text-center">
              <p className="text-[12px] font-semibold text-red-700">利润率加载失败</p>
              <button onClick={() => plQ.refetch()} disabled={plQ.isFetching} className="mt-1.5 rounded-md bg-blue-600 px-3 py-1 text-[12px] font-semibold text-white hover:bg-blue-700 disabled:opacity-40">{plQ.isFetching ? "重试中…" : "点击重试"}</button>
            </div>
          ) : (
            <>
              <div className="overflow-hidden rounded-lg border border-slate-200">
                <table className="w-full text-[13px]">
                  <thead className="bg-slate-50 text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                    <tr><th className="px-4 py-2 text-left">月份</th><th className="px-4 py-2 text-right">毛利率</th><th className="px-4 py-2 text-right">净利率</th></tr>
                  </thead>
                  <tbody>
                    {[...trend].reverse().map((t) => (
                      <tr key={t.label} className="border-t border-slate-100">
                        <td className="px-4 py-2 font-medium text-slate-800">{t.label}</td>
                        <td className={`px-4 py-2 text-right font-bold ${t.gross_margin_pct < 0 ? "text-red-600" : "text-blue-500"}`}>{t.gross_margin_pct}%</td>
                        <td className={`px-4 py-2 text-right font-bold ${t.net_margin_pct < 0 ? "text-red-600" : "text-emerald-500"}`}>{t.net_margin_pct}%</td>
                      </tr>
                    ))}
                    {trend.length === 0 && <tr><td colSpan={3} className="px-4 py-3 text-center text-slate-400">当期毛利率 {pl?.gross_margin_pct ?? "—"}% · 净利率 {pl?.net_margin_pct ?? "—"}%</td></tr>}
                  </tbody>
                </table>
              </div>
              <div className="mt-2 text-[11px] font-semibold text-emerald-600">行业基准毛利率 30-40% · 净利率 15-25% · 我们高于均值</div>
            </>
          )}
        </Card>
      </div>

      {/* 资金告警面板 · 带明细行（说明书 §三 #10） */}
      <Card title="🚨 资金告警面板" action={<span className="text-[12px] text-slate-500">触发即通知老板 + 财务</span>}>
        {(apQ.isLoading || arQ.isLoading || dashQ.isLoading) ? <LoadingState /> :
         (apQ.isError || arQ.isError || dashQ.isError) ? (
          <div className="py-6 text-center">
            {/* 🔴 告警面板绝不能在加载失败时显"无风险"·必须明确告知未能确认 */}
            <p className="text-[12px] font-semibold text-red-700">⚠️ 告警数据加载失败 · 未能确认是否有资金风险</p>
            <button onClick={() => { apQ.refetch(); arQ.refetch(); dashQ.refetch(); }} className="mt-1.5 rounded-md bg-blue-600 px-3 py-1 text-[12px] font-semibold text-white hover:bg-blue-700">点击重试</button>
          </div>
        ) : (
        <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
          <AlertTile tone={cash < 0 ? "bad" : cash >= 8_000_000 ? "good" : "warn"}
            to="/admin/finance/reports"
            title={cash < 0 ? `🔴 现金赤字 ${formatCents(cash)}` : cash >= 8_000_000 ? `🟢 现金充足 ${formatCents(cash)}` : `🟡 现金偏低 ${formatCents(cash)}`}
            lines={["警戒线 $80k", cash < 0 ? "资金缺口 · 需立即处理" : cash >= 8_000_000 ? "当前安全" : "需关注"]} />
          <AlertTile tone={apPending.length ? "warn" : "good"}
            to="/admin/finance/payables?status=pending_approval"
            title={apPending.length ? `🟡 应付账款待审 ${apPending.length} 笔` : "🟢 无待审应付"}
            lines={apPending.length ? [`总额 ${formatCents(apPending.reduce((s, p) => s + p.amount_cents, 0))} · 老板需处理`, ...apPending.slice(0, 2).map((p) => `· ${PAYABLE_KIND_LABEL[p.kind]} ${p.supplier} ${formatCents(p.amount_cents)}`)] : ["良性"]} />
          <AlertTile tone={arOverdue > 0 ? "bad" : "good"}
            to="/admin/finance/receivables?overdue=1"
            title={arOverdue > 0 ? `🔴 逾期应收 ${formatCents(arOverdue)}` : "🟢 无逾期应收"}
            lines={arOverdueRows.length ? [`${arOverdueRows.length} 客户逾期 · 已发催款`, ...arOverdueRows.slice(0, 2).map((r) => `· ${r.customer} ${formatCents(r.amount_cents)}（逾期 ${r.days_overdue} 天）`)] : ["良性"]} />
        </div>
        )}
      </Card>

      {/* 快捷入口 · 5 项各主题色（说明书 §三 #11） */}
      <Card title="🚀 快捷入口">
        <div className="grid grid-cols-2 gap-3 lg:grid-cols-5">
          <QuickLink to="/admin/finance/receivables" icon="💰" title="应收账款" sub="B2B + 直客 + API 平台 3 来源" badge={`${(arQ.data ?? []).length} 条待处理`} titleColor="text-blue-800" subColor="text-blue-500" badgeBg="bg-blue-50 text-blue-800" />
          <QuickLink to="/admin/finance/payables" icon="💸" title="应付账款" sub="酒店 / 车 / 司导 / 景点 4 类" badge={`待付 ${(apQ.data ?? []).length} · ${apPending.length} 待审`} titleColor="text-red-800" subColor="text-red-500" badgeBg="bg-red-50 text-red-800" />
          <QuickLink to="/admin/finance/invoices" icon="📄" title="发票管理" sub="Invoice 记录 + 收据归档" badge={`本月开 ${ov?.invoices_total ?? 0} 张`} titleColor="text-violet-800" subColor="text-violet-500" badgeBg="bg-violet-50 text-violet-800" />
          <QuickLink to="/admin/finance/reports" icon="📊" title="财务报表" sub="P&L + 渠道毛利 + 产品排行" badge="月报已生成" titleColor="text-emerald-800" subColor="text-emerald-500" badgeBg="bg-emerald-50 text-emerald-800" />
          <QuickLink to="/admin/finance/expenses" icon="🧾" title="司导报销" sub="司导费用报销 · 按司导查询" badge="财务专属查询" titleColor="text-amber-800" subColor="text-amber-500" badgeBg="bg-amber-50 text-amber-800" />
        </div>
      </Card>
    </PageShell>
  );
}

function AlertTile({ tone, title, lines, to }: { tone: "good" | "warn" | "bad"; title: string; lines: string[]; to?: string }) {
  const bg = tone === "good" ? "border-emerald-200 bg-emerald-50" : tone === "warn" ? "border-amber-200 bg-amber-50" : "border-rose-200 bg-rose-50";
  const tc = tone === "good" ? "text-emerald-800" : tone === "warn" ? "text-amber-800" : "text-rose-800";
  const sc = tone === "good" ? "text-emerald-700" : tone === "warn" ? "text-amber-700" : "text-rose-700";
  const inner = (
    <>
      <div className="flex items-center justify-between gap-2">
        <div className={`text-[12px] font-bold ${tc}`}>{title}</div>
        {to && <span className={`shrink-0 text-[10px] font-semibold ${sc}`}>处理 →</span>}
      </div>
      {lines.map((l, i) => <div key={i} className={`mt-0.5 text-[10px] ${sc}`}>{l}</div>)}
    </>
  );
  const cls = `block rounded-lg border ${bg} px-4 py-3`;
  return to
    ? <Link to={to} className={`${cls} transition hover:shadow-sm hover:brightness-[0.97]`}>{inner}</Link>
    : <div className={cls}>{inner}</div>;
}

function QuickLink({ to, icon, title, sub, badge, titleColor, subColor, badgeBg }: {
  to: string; icon: string; title: string; sub: string; badge: string; titleColor: string; subColor: string; badgeBg: string;
}) {
  return (
    <Link to={to} className="rounded-xl border border-slate-200 bg-white p-4 transition hover:border-blue-300 hover:shadow-sm">
      <div className="text-[18px]">{icon}</div>
      <div className={`mt-1 text-[13px] font-bold ${titleColor}`}>{title}</div>
      <div className={`mt-0.5 text-[10px] ${subColor}`}>{sub}</div>
      <div className={`mt-2 inline-block rounded-full px-2 py-0.5 text-[11px] font-semibold ${badgeBg}`}>{badge}</div>
    </Link>
  );
}
